import { useEffect, useState } from 'react'

import { formatDate } from 'commons/utils/date'
import {
  ActionTabs,
  BatchSearch,
  Flex,
  Icon,
  type ItemActionTabsType,
  LoadingHavaianas,
  NotFound,
  Select,
  SlimButton,
  Tooltip,
} from 'components'
import { availableCentersFactories, monthOptions } from 'constants/enums'
import { t } from 'i18next'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { fetchSharesBMU, setPage } from 'store/FairShareContext/fairShareSlice'
import { useDispatch, useSelector } from 'store/store'

import * as S from '../../styles'
import { BMUTable } from '../BMUTable/BMUTable'
import { EmptyShare } from '../EmptyShare'

export const BMUView = () => {
  const dispatch = useDispatch()
  const {
    loadingBMUView,
    loadingShareUpload,
    loadingMetaUpload,
    bmuFilters,
    hasOptionalFilters,
    sharesBMU: { data, businessUnits },
    pagination: { currentPage, pageSize },
  } = useSelector((state) => state.fairShare)
  const [currentBMUTab, setCurrentBMUTab] = useState('0')

  const defaultValues = {
    businessUnit: '',
    center: '',
    color: '',
    material: '',
    month: formatDate(new Date(), 'MM'),
    year: formatDate(new Date(), 'YYYY'),
  }

  const methods = useForm({
    defaultValues,
  })

  const { handleSubmit, getValues, setValue, reset } = methods

  const currentYear = new Date().getFullYear()
  const yearOptions = Array.from({ length: 6 }, (_, i) => {
    const year = (currentYear + i).toString()
    return {
      title: year,
      value: year,
    }
  })

  const centerFactoryOptions = [
    { title: t('common.all', { context: 'male' }), value: '' },
    ...availableCentersFactories.map((center) => ({
      title: center,
      value: center,
    })),
  ]

  const getPayload = () => {
    const { year, month, businessUnit, center, material, color } = getValues()
    return {
      businessUnit,
      centerCode: center,
      colorCodes: color ? color.split(',') : [],
      currentPage: currentPage - 1,
      materialCodes: material ? material.split(',') : [],
      pageSize: pageSize,
      yearMonth: Number(year + month),
    }
  }

  const handleTabChange = (index: number) => {
    dispatch(setPage(1))
    setCurrentBMUTab(`${index}`)
    const selectedBU = index === 0 ? '' : businessUnits[index - 1].code
    setValue('businessUnit', selectedBU)
    const fairSharePayload = { ...getPayload(), businessUnit: selectedBU, currentPage: 0 }
    dispatch(fetchSharesBMU(fairSharePayload))
  }

  const getTabItems = () => {
    const items: ItemActionTabsType[] = [
      {
        label: t('common.all', { context: 'female' }),
        onClick: () => handleTabChange(0),
      },
    ]
    if (businessUnits.length) {
      businessUnits.forEach((bmu, index) => {
        items.push({
          label: bmu.description,
          onClick: () => handleTabChange(index + 1),
        })
      })
    }
    return items
  }

  const handleSearch = () => {
    dispatch(setPage(1))

    // NOTE: reset BMU tab if key filter (year, month) has changed
    if (bmuFilters && bmuFilters.yearMonth !== getPayload().yearMonth) {
      setCurrentBMUTab('0')
      setValue('businessUnit', '')
    }
    const fairSharePayload = {
      ...getPayload(),
      currentPage: 0,
    }
    dispatch(fetchSharesBMU(fairSharePayload))
  }

  const handleClear = () => {
    reset()
    dispatch(setPage(1))
    setCurrentBMUTab('0')
    const fairSharePayload = {
      ...getPayload(),
      currentPage: 0,
    }
    dispatch(fetchSharesBMU(fairSharePayload))
  }

  useEffect(() => {
    const fairSharePayload = getPayload()
    dispatch(fetchSharesBMU(fairSharePayload))
  }, [currentPage, pageSize])

  return (
    <FormProvider {...methods}>
      <S.Form onSubmit={handleSubmit(handleSearch)}>
        {loadingBMUView ? (
          <Flex>
            <LoadingHavaianas />
          </Flex>
        ) : (
          <Flex direction="column" gap="xm">
            <S.GridContainerBMU>
              <S.FieldWrapper className="grid-1-4">
                <Controller
                  name="month"
                  render={({ field: { name, value, onChange } }) => (
                    <Select
                      id="month"
                      label={t('common.month')}
                      name={name}
                      onChange={(e) => onChange(e)}
                      options={monthOptions.map((option) => ({
                        ...option,
                        title: t(option.title),
                      }))}
                      value={value}
                    />
                  )}
                />
              </S.FieldWrapper>
              <S.FieldWrapper className="grid-4-7">
                <Controller
                  name="year"
                  render={({ field: { name, value, onChange } }) => (
                    <Select
                      id="year"
                      label={t('common.year')}
                      name={name}
                      onChange={(e) => onChange(e)}
                      options={yearOptions}
                      value={value}
                    />
                  )}
                />
              </S.FieldWrapper>
              <S.FieldWrapper className="grid-7-10">
                <Controller
                  name="center"
                  render={({ field: { name, value, onChange } }) => (
                    <Select
                      disabled={!data.length && !hasOptionalFilters}
                      id="center"
                      label={t('common.center')}
                      menuText={
                        data.length
                          ? t('common.placeholders.select')
                          : t('screens.fairShare.loadTheShare')
                      }
                      name={name}
                      onChange={(e) => onChange(e)}
                      options={centerFactoryOptions}
                      value={value}
                    />
                  )}
                />
              </S.FieldWrapper>
              <S.FieldWrapper className="grid-10-16">
                <Controller
                  name="material"
                  render={({ field: { value, onChange } }) => (
                    <BatchSearch
                      disabled={!data.length && !hasOptionalFilters}
                      label={t('common.material')}
                      onChange={(e) => onChange(e)}
                      onClear={() => onChange('')}
                      placeHolder={
                        data.length ? t('common.searchByCode') : t('screens.fairShare.loadTheShare')
                      }
                      value={value}
                    />
                  )}
                />
              </S.FieldWrapper>
              <S.FieldWrapper className="grid-16-22">
                <Controller
                  name="color"
                  render={({ field: { value, onChange } }) => (
                    <BatchSearch
                      disabled={!data.length && !hasOptionalFilters}
                      label={t('common.color')}
                      onChange={(e) => onChange(e)}
                      onClear={() => onChange('')}
                      placeHolder={
                        data.length ? t('common.searchByCode') : t('screens.fairShare.loadTheShare')
                      }
                      value={value}
                    />
                  )}
                />
              </S.FieldWrapper>
              <S.FieldWrapper className="grid-22-24">
                <SlimButton content={t('buttons.search')} type="submit" variant="primary" />
              </S.FieldWrapper>
              <S.FieldWrapper className="grid-24-25">
                <Tooltip arrow title={t('buttons.clear')}>
                  <Flex fullWidth>
                    <SlimButton
                      icon={<Icon icon="close" size="20" />}
                      onClick={handleClear}
                      type="button"
                      variant="border"
                    />
                  </Flex>
                </Tooltip>
              </S.FieldWrapper>
            </S.GridContainerBMU>

            {!!businessUnits.length && (
              <S.TabsWrapper>
                <ActionTabs currentItem={currentBMUTab} items={getTabItems()} />
              </S.TabsWrapper>
            )}

            {data.length ? (
              <BMUTable />
            ) : !hasOptionalFilters ? (
              <EmptyShare
                descriptionKey="bmu"
                loadingUpload={loadingShareUpload || loadingMetaUpload}
              />
            ) : (
              <NotFound
                description={
                  <Flex alignItems="center" direction="column" gap="xm">
                    {t('common.messages.noResultFromFilters')}
                    <SlimButton
                      content={t('buttons.clearFilters')}
                      icon={<Icon icon="close" size="18" />}
                      onClick={handleClear}
                      type="button"
                      variant="info"
                    />
                  </Flex>
                }
                icon={<Icon icon="bot" size="40" />}
                title={t('common.messages.opsNotFound')}
              />
            )}
          </Flex>
        )}
      </S.Form>
    </FormProvider>
  )
}
